import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/lib/baseQuery';
import type {
  DtiPolicy,
  HemBenchmark,
  IncomeTaxBracket,
  LenderPolicy,
  LenderProduct,
  StampDutyBracket,
  TaxResidency,
} from '../types';
import {
  APRA_BUFFER,
  BASE_RATE,
  BONUS_HAIRCUT,
  CC_ASSESSMENT_FACTOR,
  HECS_MONTHLY,
  LOAN_TERM_MONTHS,
  MAINT_PCT,
  MGMT_PCT,
  PURCHASE_FEES_PCT,
  RATES_INS_PCT,
  RENTAL_SHADE,
  RENTAL_YIELD,
  STRATA_PCT,
} from '../constants';

interface StateTaxRateResponse {
  id: string;
  stateCode: string;
  minPropertyValue: number | string;
  maxPropertyValue: number | string;
  baseDutyAud: number | string;
  rateOnExcessPer100: number | string;
  foreignBuyerSurchargePct: number | string;
  absenteeLandTaxPct: number | string;
  sourceName: string | null;
}

interface HemBenchmarkResponse {
  id: string;
  maritalStatus: string;
  dependants: number;
  incomeMin: number | string;
  incomeMax: number | string | null;
  minimumMonthlyExpenseAud: number | string;
}

interface LenderPolicyResponse {
  maxLvrAuCitizen: number | string | null;
  maxLvrAuPr: number | string | null;
  maxLvrForeignNational: number | string | null;
  baseSalaryShade: Record<string, number | string> | null;
  bonusSalaryShade: Record<string, number | string> | null;
  auRentalShade: number | string | null;
  foreignRentalShade: number | string | null;
  productRate: number | string | null;
  productName: string | null;
  creditCardFactor: number | string | null;
  assessmentRateBuffer: number | string | null;
  floorRate: number | string | null;
  dti: DtiPolicy;
  dtiIncomeBasis?: 'assessed' | 'gross' | null;
  dscr: LenderPolicy['dscr'] | undefined;
  isCurrent: boolean;
}

function toNum(
  v: number | string | null | undefined,
  fallback: number,
): number {
  if (v == null) return fallback;
  const n = Number(v);
  return Number.isNaN(n) ? fallback : n;
}

function toNumRecord(
  rec: Record<string, number | string> | null | undefined,
  fallback: Record<string, number>,
): Record<string, number> {
  if (!rec) return fallback;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(rec)) {
    out[k] = Number(v);
  }
  return out;
}

function parseDti(raw: DtiPolicy | undefined): DtiPolicy {
  if (raw == null) return null;
  if (typeof raw === 'number' || typeof raw === 'string') return Number(raw);
  return {
    ownerOccupiedNoRental: Number(raw.ownerOccupiedNoRental),
    ownerOccupiedWithRental: Number(raw.ownerOccupiedWithRental),
    investment: Number(raw.investment),
  };
}

function parseDscr(
  raw: LenderPolicy['dscr'] | undefined,
): LenderPolicy['dscr'] {
  if (!raw) return null;
  const result: LenderPolicy['dscr'] = { minimum: Number(raw.minimum) };
  if (raw.aboveLvr) {
    result!.aboveLvr = {
      lvr: Number(raw.aboveLvr.lvr),
      minimum: Number(raw.aboveLvr.minimum),
    };
  }
  return result;
}

// Normalise the embedded products/pricings (present with includePricing=true).
// `finalInterestRate` arrives as a percentage figure (e.g. "6.000") and is
// converted to a decimal fraction; LVR bounds stay as percentage figures.
function normaliseProducts(
  products: LenderProductResponse[] | undefined,
): LenderProduct[] {
  if (!Array.isArray(products)) return [];
  return products.map((p) => ({
    name: p.name,
    applicantType: p.applicantType,
    interestType: p.interestType,
    isActive: p.isActive,
    pricings: (p.pricings ?? []).map((pr) => ({
      id: pr.id,
      repaymentType: pr.repaymentType,
      lvrMin: pr.lvrMin != null ? Number(pr.lvrMin) : null,
      lvrMax: pr.lvrMax != null ? Number(pr.lvrMax) : null,
      finalInterestRate:
        pr.finalInterestRate != null
          ? Number(pr.finalInterestRate) / 100
          : null,
      isActive: pr.isActive,
    })),
  }));
}

// Product pricing, embedded only when the lenders endpoint is called with
// `includePricing=true`. Decimal fields arrive as strings from the API.
// (Mirrors backend LenderProductPricingResponseDto.)
interface LenderProductPricingResponse {
  id: string;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  repaymentType: string; // RepaymentType enum, e.g. 'principal_and_interest'
  lvrMin: number | string | null;
  lvrMax: number | string | null;
  loanAmountMin: number | string | null;
  loanAmountMax: number | string | null;
  baseInterestRate: number | string | null;
  discountRate: number | string | null;
  /** Derived: baseInterestRate - discountRate (percentage figure, e.g. "6.000"). */
  finalInterestRate: number | string | null;
  isActive: boolean;
  note: string | null;
  version: number;
  createdByUserName: string | null;
  updatedByUserName: string | null;
  updatedAt: string | null;
}

// One product with its pricing rows. (Mirrors backend LenderProductResponseDto.)
interface LenderProductResponse {
  id: string;
  lenderId: string;
  name: string;
  applicantType: string; // ApplicantType enum, e.g. 'EXPAT' | 'FOREIGNER'
  interestType: string; // InterestType enum, e.g. 'variable' | 'fixed'
  isActive: boolean;
  pricings: LenderProductPricingResponse[];
}

interface LenderResponse {
  id: string;
  name: string;
  displayName: string;
  isActive: boolean;
  logoUrl: string | null;
  tier: number | null;
  policies: LenderPolicyResponse[];
  // Present only when fetched with `includePricing=true`. Not yet consumed by
  // the transform below — wired in a later step.
  products?: LenderProductResponse[];
}

interface ForexRatesResponse {
  base: string;
  last_updated: string;
  next_update: string;
  rates: Record<string, number>;
}

export interface ForexRates {
  base: string;
  lastUpdated: string;
  nextUpdate: string;
  rates: Record<string, number>;
}

interface AtoTaxBracketResponse {
  id: string;
  taxResidency: string;
  lowerIncomeThreshold: number | string;
  upperIncomeThreshold: number | string;
  baseTaxAud: number | string;
  taxRateOnExcess: number | string;
}

interface CalculationSettingResponse {
  id: string;
  category: string;
  key: string;
  value: number | string | null;
  valueJson: unknown | null;
  label: string | null;
}

/**
 * The brokercopilot backend wraps every response in an
 * `ApiResponse` envelope: `{ statusCode, message, data }`. The public
 * settings/lender endpoints return their payload at `.data`. This helper
 * unwraps either a bare payload or the envelope so the normalizers below
 * stay agnostic to the transport shape.
 */
function unwrap<T>(raw: T | { data: T }): T {
  if (raw && typeof raw === 'object' && 'data' in (raw as object)) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

/**
 * Threshold params for a configurable rule. Numbers for bounds
 * (`min`/`max`/`equals`/…); string arrays for set membership
 * (`currencies`/`supported`).
 */
export type RuleParams = Record<string, number | string[]>;

/** One configurable result-page rule (backend-owned knobs; predicate stays in code). */
export interface RuleConfig {
  weight: number;
  active: boolean;
  params: RuleParams;
  text: string;
}

/** A `rules/*` blob stored in calculation_settings.value_json (strengths, considerations). */
export interface RuleSetBlob {
  topN: number;
  rules: Record<string, RuleConfig>;
}

/** One configurable warning. Like RuleConfig but with severity + display order, no weight/topN. */
export interface WarningConfig {
  active: boolean;
  severity: 'high' | 'medium' | 'low';
  sortOrder: number;
  params: RuleParams;
  text: string;
  /** Optional escalated severity when the rule's `escalateMin` threshold is exceeded. */
  escalateSeverity?: 'high' | 'medium' | 'low';
}

/** The `rules/warnings` blob: every active+passing warning is shown, ordered by `sortOrder`. */
export interface WarningSetBlob {
  rules: Record<string, WarningConfig>;
}

/**
 * The `rules/messages` blob: single-select copy keyed by a discriminant.
 * `headline` keyed by status (green/amber/red); `taxNote` keyed by
 * foreign_national/investment/owner_occupied. `params` holds shared figures
 * (e.g. FRCGW %, CGT discount %) interpolated into the templates.
 */
export interface MessagesBlob {
  headline: Record<string, string>;
  taxNote: Record<string, string>;
  params: Record<string, number>;
}

export interface CalculationSettings {
  capitalGains: {
    cg5yrRate: number;
    cg7yrRate: number;
  };
  investmentCashflow: {
    maintPct: number;
    mgmtPct: number;
    ratesInsPct: number;
    strataPct: number;
  };
  liability: {
    ccAssessmentFactor: number;
    hecsMonthly: number;
  };
  serviceability: {
    apraBuffer: number;
    baseRate: number;
    bonusHaircut: number;
    globalBaseShade: number;
    loanTermMonths: number;
    purchaseFeesPct: number;
    rentalShade: number;
    rentalYield: number;
    /** Global rental-income inclusion shade for investment serviceability (v2 = 0.90). */
    rentalIncludeGlobal: number;
    /** Monthly surplus below which status flips green → amber. */
    surplusAmberThreshold: number;
  };
  /** Configurable result-page rule sets (predicate logic stays code-side). */
  rules?: {
    strengths?: RuleSetBlob;
    considerations?: RuleSetBlob;
    messages?: MessagesBlob;
    warnings?: WarningSetBlob;
  };
}

export const blueprintApi = createApi({
  reducerPath: 'blueprintApi',
  baseQuery: customBaseQuery,
  endpoints: (builder) => ({
    fetchStampDutyBrackets: builder.query<
      Record<string, StampDutyBracket[]>,
      void
    >({
      query: () => 'v1/public/settings/state-tax-rates',
      transformResponse: (
        raw: StateTaxRateResponse[] | { data: StateTaxRateResponse[] },
      ) => {
        const data = unwrap(raw);
        const grouped: Record<string, StampDutyBracket[]> = {};
        for (const row of data) {
          const code = row.stateCode;
          if (!grouped[code]) grouped[code] = [];
          grouped[code].push({
            minPropertyValue: Number(row.minPropertyValue),
            maxPropertyValue: Number(row.maxPropertyValue),
            baseDutyAud: Number(row.baseDutyAud),
            rateOnExcessPer100: Number(row.rateOnExcessPer100),
            foreignBuyerSurchargePct: Number(row.foreignBuyerSurchargePct),
          });
        }
        for (const code of Object.keys(grouped)) {
          grouped[code].sort((a, b) => a.minPropertyValue - b.minPropertyValue);
        }
        return grouped;
      },
    }),

    fetchHemBenchmarks: builder.query<HemBenchmark[], void>({
      query: () => 'v1/public/settings/hem-benchmarks',
      transformResponse: (
        json: HemBenchmarkResponse[] | { data: HemBenchmarkResponse[] },
      ) => {
        const rows = unwrap(json);
        return rows.map((r) => ({
          maritalStatus: r.maritalStatus,
          dependants: r.dependants,
          incomeMin: Number(r.incomeMin),
          incomeMax: r.incomeMax === null ? null : Number(r.incomeMax),
          minimumMonthlyExpenseAud: Number(r.minimumMonthlyExpenseAud),
        }));
      },
    }),

    fetchFxRates: builder.query<ForexRates, void>({
      query: () => 'forex/rates',
      transformResponse: (
        raw: ForexRatesResponse | { data: ForexRatesResponse },
      ): ForexRates => {
        const data = unwrap(raw);
        return {
          base: data.base,
          lastUpdated: data.last_updated,
          nextUpdate: data.next_update,
          rates: data.rates,
        };
      },
    }),

    fetchCalculationSettings: builder.query<CalculationSettings, void>({
      query: () => 'v1/public/settings/calculation-settings',
      transformResponse: (
        raw:
          | CalculationSettingResponse[]
          | { data: CalculationSettingResponse[] },
      ): CalculationSettings => {
        const rows = unwrap(raw);
        const lookup = new Map<string, number>();
        let strengthsBlob: RuleSetBlob | undefined;
        let considerationsBlob: RuleSetBlob | undefined;
        let messagesBlob: MessagesBlob | undefined;
        let warningsBlob: WarningSetBlob | undefined;
        for (const row of rows) {
          // Structured (jsonb) rows carry config, not a number — handle and skip.
          if (row.category === 'rules') {
            if (row.key === 'strengths') {
              strengthsBlob =
                (row.valueJson as RuleSetBlob | null) ?? undefined;
            } else if (row.key === 'considerations') {
              considerationsBlob =
                (row.valueJson as RuleSetBlob | null) ?? undefined;
            } else if (row.key === 'messages') {
              messagesBlob =
                (row.valueJson as MessagesBlob | null) ?? undefined;
            } else if (row.key === 'warnings') {
              warningsBlob =
                (row.valueJson as WarningSetBlob | null) ?? undefined;
            }
            continue;
          }
          if (row.value == null) continue;
          const n = Number(row.value);
          if (!Number.isNaN(n)) {
            lookup.set(`${row.category}.${row.key}`, n);
          }
        }
        const get = (category: string, key: string, fallback: number) =>
          lookup.get(`${category}.${key}`) ?? fallback;
        return {
          capitalGains: {
            cg5yrRate: get('capital_gains', 'cg_5yr_rate', 0.05),
            cg7yrRate: get('capital_gains', 'cg_7yr_rate', 0.07),
          },
          investmentCashflow: {
            maintPct: get('investment_cashflow', 'maint_pct', MAINT_PCT),
            mgmtPct: get('investment_cashflow', 'mgmt_pct', MGMT_PCT),
            ratesInsPct: get(
              'investment_cashflow',
              'rates_ins_pct',
              RATES_INS_PCT,
            ),
            strataPct: get('investment_cashflow', 'strata_pct', STRATA_PCT),
          },
          liability: {
            ccAssessmentFactor: get(
              'liability',
              'cc_assessment_factor',
              CC_ASSESSMENT_FACTOR,
            ),
            hecsMonthly: get('liability', 'hecs_monthly', HECS_MONTHLY),
          },
          serviceability: {
            apraBuffer: get('serviceability', 'apra_buffer', APRA_BUFFER),
            baseRate: get('serviceability', 'base_rate', BASE_RATE),
            bonusHaircut: get('serviceability', 'bonus_haircut', BONUS_HAIRCUT),
            globalBaseShade: get('serviceability', 'global_base_shade', 1.0),
            loanTermMonths: get(
              'serviceability',
              'loan_term_months',
              LOAN_TERM_MONTHS,
            ),
            purchaseFeesPct: get(
              'serviceability',
              'purchase_fees_pct',
              PURCHASE_FEES_PCT,
            ),
            rentalShade: get('serviceability', 'rental_shade', RENTAL_SHADE),
            rentalYield: get('serviceability', 'rental_yield', RENTAL_YIELD),
            rentalIncludeGlobal: get(
              'serviceability',
              'rental_include_global',
              0.9,
            ),
            surplusAmberThreshold: get(
              'serviceability',
              'surplus_amber_threshold',
              1500,
            ),
          },
          rules: {
            strengths: strengthsBlob,
            considerations: considerationsBlob,
            messages: messagesBlob,
            warnings: warningsBlob,
          },
        };
      },
    }),

    fetchIncomeTaxBrackets: builder.query<IncomeTaxBracket[], void>({
      query: () => 'v1/public/settings/ato-tax-brackets',
      transformResponse: (
        raw: AtoTaxBracketResponse[] | { data: AtoTaxBracketResponse[] },
      ): IncomeTaxBracket[] => {
        const rows = unwrap(raw);
        return rows.map((r) => ({
          taxResidency: r.taxResidency as TaxResidency,
          lowerIncomeThreshold: String(r.lowerIncomeThreshold),
          upperIncomeThreshold: String(r.upperIncomeThreshold),
          baseTaxAud: String(r.baseTaxAud),
          taxRateOnExcess: String(r.taxRateOnExcess),
        }));
      },
    }),

    fetchBlueprintLenders: builder.query<LenderPolicy[], void>({
      query: () =>
        'v1/public/lender-management/lenders?calculationEnabled=true&includePricing=true',
      transformResponse: (
        raw: LenderResponse[] | { data: LenderResponse[] },
      ) => {
        const data = unwrap(raw);
        return data
          .filter((lender) => lender.isActive)
          .map((lender) => {
            const policy =
              lender.policies.find((p) => p.isCurrent) ?? lender.policies[0];
            const maxLvrCitizen = policy?.maxLvrAuCitizen;
            const maxLvrPr = policy?.maxLvrAuPr;
            const maxLvrForeign = policy?.maxLvrForeignNational;
            return {
              name: lender.name,
              displayName: lender.displayName,
              tier:
                lender.tier === 1 || lender.tier === 2 || lender.tier === 3
                  ? lender.tier
                  : 3,
              logoUrl: lender.logoUrl ?? null,
              maxLvrAuCitizen:
                maxLvrCitizen != null ? Number(maxLvrCitizen) : null,
              maxLvrAuPr: maxLvrPr != null ? Number(maxLvrPr) : null,
              maxLvrForeignNational:
                maxLvrForeign != null ? Number(maxLvrForeign) : null,
              baseSalaryShade: toNumRecord(policy?.baseSalaryShade, {
                AUD: 1.0,
                _: 0.6,
              }),
              bonusSalaryShade: toNumRecord(policy?.bonusSalaryShade, {
                AUD: 0.25,
                _: 0.25,
              }),
              auRentalShade: toNum(policy?.auRentalShade, 0.8),
              foreignRentalShade: toNum(policy?.foreignRentalShade, 0.8),
              productRate: toNum(policy?.productRate, 0.065),
              productName: policy?.productName ?? 'Standard Variable',
              creditCardFactor: toNum(policy?.creditCardFactor, 0.038),
              assessmentRateBuffer: toNum(policy?.assessmentRateBuffer, 0.03),
              floorRate: toNum(policy?.floorRate, 0),
              dti: parseDti(policy?.dti),
              dtiIncomeBasis: policy?.dtiIncomeBasis ?? undefined,
              dscr: parseDscr(policy?.dscr),
              products: normaliseProducts(lender.products),
            };
          });
      },
    }),
  }),
});

export const {
  useFetchStampDutyBracketsQuery,
  useFetchHemBenchmarksQuery,
  useFetchBlueprintLendersQuery,
  useFetchFxRatesQuery,
  useFetchCalculationSettingsQuery,
  useFetchIncomeTaxBracketsQuery,
} = blueprintApi;
